import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../store/authStore';
import { Logo } from './Logo';
import glassTheme from '../styles/glassTheme';

const PageContainer = styled.div`
  min-height: 100vh;
  ${glassTheme.gridBackground}
  padding: 2rem 0;
`;

const Navigation = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$compact' && prop !== '$analytics'
})<{ $compact?: boolean; $analytics?: boolean }>`
  ${glassTheme.glass.container}
  --dashboard-nav-gradient-text: ${props => props.$analytics ? glassTheme.colors.background : glassTheme.colors.text.primary};
  padding: 1rem 2rem;
  margin-bottom: ${props => props.$compact ? glassTheme.spacing.lg : glassTheme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${glassTheme.spacing.md};
`;

const NavLogo = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: ${glassTheme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const NavButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active?: boolean }>`
  padding: 0.6rem 1.2rem;
  background: ${props => props.active
    ? glassTheme.gradients.cyber
    : 'transparent'};
  border: 1px solid ${props => props.active
    ? 'transparent'
    : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${props => props.active ? 'var(--dashboard-nav-gradient-text)' : glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-size: 0.9rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    color: var(--dashboard-nav-gradient-text);
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

const NavLink = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active?: boolean }>`
  padding: 0.6rem 1.2rem;
  background: ${props => props.active
    ? glassTheme.gradients.cyber
    : 'transparent'};
  border: 1px solid ${props => props.active
    ? 'transparent'
    : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${props => props.active ? 'var(--dashboard-nav-gradient-text)' : glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    color: var(--dashboard-nav-gradient-text);
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${glassTheme.spacing.xl} ${glassTheme.spacing.xl};
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding-inline: ${glassTheme.spacing.md};
  }
`;

const Header = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$compact'
})<{ $compact?: boolean }>`
  padding: ${props => props.$compact ? glassTheme.spacing.md : glassTheme.spacing.xl} 0;
  margin-bottom: ${props => props.$compact ? glassTheme.spacing.md : glassTheme.spacing.xl};
  text-align: center;
`;

const Title = styled.h1.withConfig({
  shouldForwardProp: (prop) => prop !== '$compact'
})<{ $compact?: boolean }>`
  font-size: ${props => props.$compact ? '2rem' : '2.5rem'};
  line-height: 1.2;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: ${glassTheme.colors.text.secondary};
  font-size: 1.1rem;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  ${glassTheme.glass.light}
  padding: 2rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
    border-color: ${glassTheme.colors.borderHover};
  }
`;

const CardIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  background: ${glassTheme.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 1rem;
`;

const CardValue = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  color: ${glassTheme.colors.text.muted};
  font-size: 0.9rem;
`;

const ActionButton = styled.button`
  padding: 0.6rem 1.2rem;
  background: ${glassTheme.gradients.cyber};
  border: none;
  border-radius: ${glassTheme.borderRadius.lg};
  color: ${glassTheme.colors.text.primary};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${glassTheme.shadow.md};
  }
`;

interface DashboardLayoutProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  compact?: boolean;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title,
  subtitle,
  children,
  compact = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const analyticsNavigation = /^\/teacher\/courses\/[^/]+\/(?:analytics|students)(?:\/|$)/.test(location.pathname) ||
    (location.pathname === '/teacher/dashboard' && new URLSearchParams(location.search).get('view') === 'students');

  return (
    <PageContainer>
      <Container>
        <Navigation $compact={compact} $analytics={analyticsNavigation} data-dashboard-navigation>
          <Logo />
          <NavLinks>
            {/* Get user's primary role and current path */}
            {(() => {
              const primaryRole = user?.isAdmin
                ? 'admin'
                : user?.primaryRole || user?.roles?.[0]?.name || 'student';
              const currentPath = location.pathname;
              const isDashboard = currentPath === '/dashboard' ||
                                 currentPath === '/student/dashboard' ||
                                 currentPath === '/teacher/dashboard' ||
                                 currentPath === '/admin/dashboard' ||
                                 currentPath === '/content-manager/dashboard';

              if (primaryRole === 'teacher') {
                return (
                  <>
                    <NavLink to="/learn" active={currentPath === '/learn'}>
                      学习平台
                    </NavLink>
                    <NavLink to="/teacher/courses" active={currentPath.startsWith('/teacher/courses')}>
                      课程管理
                    </NavLink>
                    <NavLink to="/teacher/dashboard" active={isDashboard}>
                      仪表盘
                    </NavLink>
                    <NavLink to="/teacher/dashboard?view=students&page=1" active={currentPath.includes('/analytics') || currentPath.includes('/students')}>
                      学习记录
                    </NavLink>
                  </>
                );
              } else if (primaryRole === 'student') {
                return (
                  <>
                    <NavLink to="/learn" active={currentPath === '/learn'}>
                      学习平台
                    </NavLink>
                    <NavLink to="/student/dashboard" active={isDashboard}>
                      仪表盘
                    </NavLink>
                  </>
                );
              } else if (primaryRole === 'content_manager') {
                return (
                  <>
                    <NavLink to="/learn" active={currentPath === '/learn'}>
                      学习平台
                    </NavLink>
                    <NavLink to="/content-manager/content" active={currentPath.startsWith('/content-manager/content')}>
                      内容管理
                    </NavLink>
                    <NavLink to="/content-manager/dashboard" active={isDashboard}>
                      仪表盘
                    </NavLink>
                  </>
                );
              } else {
                // admin and super_admin
                return (
                  <>
                    <NavLink to="/learn" active={currentPath === '/learn'}>
                      学习平台
                    </NavLink>
                    <NavLink to="/admin/content" active={currentPath.startsWith('/admin/content')}>
                      内容管理
                    </NavLink>
                    <NavLink to="/admin/dashboard" active={isDashboard}>
                      管理后台
                    </NavLink>
                  </>
                );
              }
            })()}
            <NavLink
              to={(() => {
                const primaryRole = user?.isAdmin
                  ? 'admin'
                  : user?.primaryRole || user?.roles?.[0]?.name || 'student';
                return primaryRole === 'admin' || primaryRole === 'super_admin' ? '/admin/profile' : '/profile';
              })()}
              active={(() => {
                const currentPath = location.pathname;
                return currentPath === '/profile' || currentPath === '/admin/profile';
              })()}
            >
              个人资料
            </NavLink>
            <NavButton
              type="button"
              onClick={(e) => {
                e.preventDefault();
                logout();
              }}
            >
              退出登录
            </NavButton>
          </NavLinks>
        </Navigation>

        <Header $compact={compact}>
          <Title $compact={compact}>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
        </Header>

        {children}
      </Container>
    </PageContainer>
  );
};

export {
  Card,
  Grid,
  CardIcon,
  CardTitle,
  CardValue,
  CardDescription,
  ActionButton
};
